import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesAccountsTypeahead,
  getV1CompaniesCompanyIdDepartmentsTypeahead,
  getV1CompaniesCompanyIdEquipmentTypesTypeahead,
  getV1CompaniesCompanyIdIamRoles,
  getV1CompaniesCompanyIdMaterialTypeahead,
  getV1CompaniesCompanyIdMaterialTypes,
  MaterialState,
} from '@treadinc/horizon-api-spec';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { AccountTypeahead } from '~hooks/useAccount/models';
import { DepartmentTypeahead } from '~hooks/useDepartment/models';
import { EquipmentTypeTypeahead } from '~hooks/useEquipment/models';
import { MaterialType, MaterialTypeahead } from '~hooks/useMaterials/models';
import { RoleItem } from '~hooks/useUsers/models';
import reportRequestError from '~services/reportRequestError';
import { fetchAllApiPages, PageQuery, TooManyRecordsError } from '~utils/fetchAllPages';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

export interface CsvImportLookups {
  connectedAccounts: AccountTypeahead[];
  departments: DepartmentTypeahead[];
  equipmentTypes: EquipmentTypeTypeahead[];
  materials: MaterialTypeahead[];
  materialTypes: MaterialType[];
  roles: RoleItem[];
}

export type CsvImportLookupName = keyof CsvImportLookups;

export type CsvImportLookupErrors = Partial<Record<CsvImportLookupName, string>>;

const CSV_IMPORT_LOOKUPS_QUERY_KEY = 'csvImportLookups';

const EMPTY_LOOKUPS: CsvImportLookups = {
  connectedAccounts: [],
  departments: [],
  equipmentTypes: [],
  materials: [],
  materialTypes: [],
  roles: [],
};

const LOOKUP_ERROR_MESSAGE_KEYS: Record<CsvImportLookupName, string> = {
  connectedAccounts: 'error_messages.account.failed_to_fetch_company_accounts',
  departments: 'error_messages.departments.failed_to_fetch',
  equipmentTypes: 'error_messages.equipment_types.failed_to_fetch',
  materials: 'error_messages.materials.failed_to_fetch',
  materialTypes: 'error_messages.material_types.failed_to_fetch',
  roles: 'error_messages.user_roles.failed_to_fetch',
};

// Name matching holds every record in memory; a company past this is not a CSV import case.
const MAX_LOOKUP_RECORDS = 10_000;

const companyLookupFetcher =
  <TItem, TRecord>(
    request: (options: {
      path: { 'company-id': string };
      query: PageQuery;
      signal?: AbortSignal;
    }) => Promise<AxiosResponse<{ data: TItem[] }>>,
    parse: (item: TItem) => TRecord,
  ) =>
  (companyId: string, signal: AbortSignal) =>
    fetchAllApiPages(
      (query, pageSignal) =>
        request({ path: { 'company-id': companyId }, query, signal: pageSignal }),
      parse,
      {
        maxRecords: MAX_LOOKUP_RECORDS,
        signal,
      },
    );

const lookupFetchers: {
  [Name in CsvImportLookupName]: (
    companyId: string,
    signal: AbortSignal,
  ) => Promise<CsvImportLookups[Name]>;
} = {
  connectedAccounts: companyLookupFetcher(
    ({ path, query, signal }) =>
      getV1CompaniesAccountsTypeahead({
        path,
        query: { ...query, 'filter[connected]': true },
        signal,
      }),
    AccountTypeahead.parse,
  ),
  departments: companyLookupFetcher(
    getV1CompaniesCompanyIdDepartmentsTypeahead,
    DepartmentTypeahead.parse,
  ),
  equipmentTypes: companyLookupFetcher(
    getV1CompaniesCompanyIdEquipmentTypesTypeahead,
    EquipmentTypeTypeahead.parse,
  ),
  materials: companyLookupFetcher(
    ({ path, query, signal }) =>
      getV1CompaniesCompanyIdMaterialTypeahead({
        path,
        query: {
          ...query,
          'filter[states]': [MaterialState.ACTIVE, MaterialState.INACTIVE],
        },
        signal,
      }),
    MaterialTypeahead.parse,
  ),
  materialTypes: companyLookupFetcher(
    getV1CompaniesCompanyIdMaterialTypes,
    MaterialType.parse,
  ),
  roles: async (companyId, signal) =>
    (
      await getV1CompaniesCompanyIdIamRoles({
        path: { 'company-id': companyId },
        signal,
      })
    ).data.data.map(RoleItem.parse),
};

const fetchLookups = async (
  companyId: string,
  lookupNames: CsvImportLookupName[],
  signal: AbortSignal,
) => {
  const errors: CsvImportLookupErrors = {};
  const lookups = { ...EMPTY_LOOKUPS };
  const loadLookup = async <Name extends CsvImportLookupName>(name: Name) => {
    try {
      lookups[name] = await lookupFetchers[name](companyId, signal);
    } catch (error) {
      if (signal.aborted) {
        throw error;
      }

      errors[name] =
        error instanceof TooManyRecordsError
          ? error.message
          : t(LOOKUP_ERROR_MESSAGE_KEYS[name]);

      reportRequestError(error, errors[name]);
    }
  };
  await Promise.all(lookupNames.map(loadLookup));

  return { errors, lookups };
};

const csvImportLookupsQueryOptions = (
  companyId: string,
  lookupNames: CsvImportLookupName[],
) => ({
  queryKey: [CSV_IMPORT_LOOKUPS_QUERY_KEY, companyId, ...lookupNames],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchLookups(companyId, lookupNames, signal),
});

interface UseCsvImportLookupsQueryProps {
  companyId: string;
  isEnabled: boolean;
  lookupNames: CsvImportLookupName[];
}

export default function useCsvImportLookupsQuery({
  companyId,
  isEnabled,
  lookupNames,
}: UseCsvImportLookupsQueryProps) {
  const queryClient = useQueryClient();
  const queryOptions = csvImportLookupsQueryOptions(companyId, lookupNames);

  const { data } = useQuery({
    ...queryOptions,
    queryFn: isEnabled ? queryOptions.queryFn : skipToken,
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  // An import must resolve names against records created moments ago, so never the cache.
  const loadLookups = useCallback(
    () =>
      queryClient.fetchQuery({
        ...csvImportLookupsQueryOptions(companyId, lookupNames),
        staleTime: 0,
      }),
    [companyId, lookupNames, queryClient],
  );

  return { loadLookups, lookups: data?.lookups ?? EMPTY_LOOKUPS };
}
